// Invitation admin client (PB-1597 / AC-10).
//
//   POST /api/auth/admin/invitations                      → create + one-time URL
//   GET  /api/auth/admin/invitations                      → cursor-paginated list
//   POST /api/auth/admin/invitations/{id}/revoke          → revoke
//
// Mirrors services/auth/src/admin/dto/invitation.response.ts. Note the shape
// asymmetry: `inviteUrl` exists on the create response and on nothing else,
// because the server stores only a hash of its token.
//
// PB-4922 adds the invitation EMAIL, so the link now normally reaches the
// invitee directly — but the field STAYS, because a workspace with no mail
// sender configured sends nothing and the administrator is then the only route
// the invitation has. Mail is the default; this is the fallback.

import { isRedeemableLocalEmail } from "@playbookai/types";
import { AdminApiError, request } from "./admin-users-api.js";
import type { TenantRole } from "./admin-users-api.js";

/** `expired` is derived by the server from `expiresAt`; only the first two are
    stored, so never treat this union as the persisted set. */
export type InviteStatus = "pending" | "revoked" | "expired" | "accepted";

/**
 * Which route may redeem an invite (PB-2926 / AC-15).
 *
 * `microsoft_entra` — the person signs in with their corporate account and the
 * customer's own directory asserts their address. The emailed link is a
 * convenience; forwarding it grants the recipient nothing.
 *
 * `local` — a password account. The token in the email IS the credential, so this
 * route is only as strong as the mailbox it was sent to.
 */
export type InviteAuthMethod = "microsoft_entra" | "local";

export interface Invite {
  invitationId: string;
  email: string;
  role: TenantRole;
  grantTransformationLead: boolean;
  status: InviteStatus;
  /* OPTIONAL for the same one release the server-side omission fallback covers. An
     old Auth pod's list response carries neither this nor `acceptedAt`, so declaring
     them required would assert something the wire does not guarantee during a
     rollout. Both become required when the field does, once no old pod is running. */
  intendedAuthenticationMethod?: InviteAuthMethod;
  expiresAt: string;
  createdAt: string;
  createdByUserId: string;
  createdByName: string | null;
  revokedAt: string | null;
  acceptedAt?: string | null;
}

export interface InvitePage {
  invitations: Invite[];
  nextCursor: string | null;
}

export interface CreatedInvite {
  invitation: Invite;
  /** The full invite URL. Returned once — there is no endpoint that re-reads it. */
  inviteUrl: string;
}

export { AdminApiError };

/* Matches the endpoint's MAX_LIMIT: it 400s above this rather than clamping. */
const INVITE_PAGE_SIZE = 50;

/** Mirrors EMAIL_MAX in the auth controller and the column CHECK. */
const EMAIL_MAX = 320;

/** The STORED lifecycle, which is what the endpoint filters on. `expired` is
    absent on purpose: the server derives it per row from `expiresAt`, so it is a
    presented status and not a filterable one. */
export type InviteStatusFilter = "pending" | "revoked";
export type InviteExpiryFilter = "active" | "expired";

export interface InviteFilters {
  /** Case-insensitive substring on the address. Blank means no filter. */
  readonly query?: string;
  readonly status?: InviteStatusFilter;
  readonly expiry?: InviteExpiryFilter;
}

/**
 * The tenant-scoped PREFIX of every invitation key — tenant-scoped so a session
 * swap cannot serve another tenant's invitations out of the cache (the PB-1556
 * class of bug).
 *
 * Invalidate with this, not with a fully-specified key. TanStack matches keys by
 * prefix, so this clears every filter combination the admin has visited; a key
 * that included the filters would clear only the view they happen to be on and
 * leave a stale page behind the filter dropdown.
 */
export function invitesQueryKeyPrefix(
  tenantId: string | null | undefined,
): readonly unknown[] {
  return ["auth", "admin", "invitations", tenantId ?? "none"] as const;
}

/**
 * The key for ONE filtered page set, used with `useInfiniteQuery`. Filters are
 * part of it because a filter change must start a new page set rather than
 * append a differently-filtered page to the last one.
 *
 * The `"page"` segment is a KIND discriminator, and it is load-bearing. The tab
 * badge reads the same endpoint through a plain `useQuery`; sharing a key with
 * an infinite query hands TanStack un-paged data where it expects `.pages`, and
 * `getNextPageParam` throws on mount. A discriminator the caller cannot supply
 * makes that collision unrepresentable rather than merely unlikely — a bare
 * filter suffix would still collide for an admin searching the word "count".
 */
export function invitesQueryKey(
  tenantId: string | null | undefined,
  filters: InviteFilters = {},
): readonly unknown[] {
  return [
    ...invitesQueryKeyPrefix(tenantId),
    "page",
    filters.query ?? "",
    filters.status ?? "all",
    filters.expiry ?? "any-expiry",
  ] as const;
}

/** The key for the tab-badge count — a plain query, so a separate kind from the
    page sets above. Still under the shared prefix, so one invalidation refreshes
    the badge and every filtered list together. */
export function invitesCountQueryKey(
  tenantId: string | null | undefined,
  status: InviteStatusFilter,
  expiry?: InviteExpiryFilter,
): readonly unknown[] {
  return [
    ...invitesQueryKeyPrefix(tenantId),
    "count",
    status,
    expiry ?? "any-expiry",
  ] as const;
}

export async function listInvites(
  cursor?: string | null,
  filters: InviteFilters = {},
): Promise<InvitePage> {
  const params = new URLSearchParams({ limit: String(INVITE_PAGE_SIZE) });
  if (cursor) params.set("cursor", cursor);
  /* Trimmed and dropped when empty, so a cleared search box sends no parameter
     at all. The server treats blank as no filter too — this just avoids a
     round trip that says nothing. */
  const query = filters.query?.trim();
  if (query) params.set("query", query);
  if (filters.status) params.set("status", filters.status);
  if (filters.expiry) params.set("expiry", filters.expiry);
  return (await request(
    `/api/auth/admin/invitations?${params.toString()}`,
  )) as InvitePage;
}

export async function createInvite(
  email: string,
  role: TenantRole,
  grantTransformationLead = false,
): Promise<CreatedInvite> {
  /* Trimmed HERE, at the client boundary, not only at the call site. The bound in
     `validateInviteEmail` is stated against the value this function puts on the
     wire, so the trim has to live where that promise is made — otherwise a future
     caller passing an unvalidated field value stores a padded address, and the
     address is the invitation's identity on the server. The caller-side trim
     stays and is now simply redundant. */
  /* PB-4922. `local` is now the ONLY route, and it is always sent explicitly.
     There is no longer a caller-supplied choice: the corporate-sign-in
     INVITATION was withdrawn because SCIM and the directory picker already
     provision those people, and mailing a link that grants nothing is worse than
     not offering it.

     This DROPS a deliberate mixed-version property, and dropping it is the point
     rather than a side effect. The field used to be omitted on the corporate
     route so the common request stayed byte-identical to what an OLD Auth pod
     accepts — old Auth rejects unknown keys outright ("Only email and role are
     accepted."), so a new Shell reaching an old pod mid-rollout would 400 every
     invitation. Now every invitation carries the field, so during a rollout
     window a new Shell against an old Auth pod fails.

     That was already true for `local` and was already argued to be correct there:
     a password-account invitation is a mailed credential, and an old pod has
     neither the route nor the policy that gates it. Failing loudly beats issuing
     a credential on a server that cannot honour the choice. What changes is that
     the failure now covers every invitation rather than one route — which is the
     honest consequence of there being only one route. */
  return (await request("/api/auth/admin/invitations", {
    method: "POST",
    body: JSON.stringify({
      email: email.trim(),
      role,
      intendedAuthenticationMethod: "local",
      grantTransformationLead,
    }),
  })) as CreatedInvite;
}

export async function revokeInvite(invitationId: string): Promise<Invite> {
  return (await request(
    `/api/auth/admin/invitations/${encodeURIComponent(invitationId)}/revoke`,
    { method: "POST" },
  )) as Invite;
}

/** Client-side pre-check calling the server's own rule — deliberately not RFC
    5322. The server stays authoritative and re-checks every request; this
    exists so the dialog can refuse before a round trip. */
export function validateInviteEmail(value: string): string | null {
  const trimmed = value.trim();
  if (trimmed.length === 0) return "Enter an email address.";
  /* Bounded on the TRIMMED value, because that is what `createInvite` puts on
     the wire — so the server sees the trimmed form as its own raw input. Bounding
     the untrimmed value here would refuse a padded 320-character address whose
     318-character payload the server accepts. */
  if (trimmed.length > EMAIL_MAX) {
    return `Email must be ${EMAIL_MAX} characters or fewer.`;
  }
  /* THE SHARED FUNCTION, not a copy of its regex. This check used to be
     `^[^@\s]+@[^@\s]+$`, which mirrored only the first of the server's two
     rules — so `a@b..com` passed here, passed there, and became a live invite
     against a domain the DNS cannot hold. A second regex maintained by hand is
     what let that happen; `@playbookai/types` now holds the one definition.

     It also carries the 255-character redeemable ceiling, so an address between
     256 and 320 characters is now refused here with this message rather than
     the server's length-specific one. Refused either way, one round trip
     sooner, and the example in the message shows the shape required. */
  if (!isRedeemableLocalEmail(trimmed)) {
    return "Enter a valid email address, like dana@northwind.io";
  }
  return null;
}
